import { supabase } from '../supabase/client';
import type { Tables } from '../supabase/types';
import { generateUsername } from '../username-utils';
import { normalizePhoneE164 } from '../phone/normalize-phone';

type UserProfile = Tables<'user_profile'>;

export interface ProfileUpdateData {
  fullname?: string;
  email?: string;
  phone?: string;
  dob?: string;
  country_id?: number;
  country_code?: string;
  state?: string;
  city?: string;
  address?: string;
  zip?: string;
  image_url?: string;
}

/**
 * Fetch user profile by user ID or email
 * Handles account linking: if profile not found by user_id, tries by email/phone
 */
export async function fetchUserProfile(userId: string): Promise<{ data: UserProfile | null; error: Error | null }> {
  try {
    // Get current user's email/phone from auth
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email;
    const userPhone = user?.phone;

    // Try to fetch by user_id first
    const { data, error } = await supabase
      .from('user_profile')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    // If found by user_id, return it
    if (data && data.length === 1) {
      return { data: data[0], error: null };
    }

    // If multiple profiles found (shouldn't happen), return first
    if (data && data.length > 1) {
      return { data: data[0], error: null };
    }

    // Profile not found by user_id, try by email or phone (account linking)
    if (userEmail || userPhone) {
      let query = supabase.from('user_profile').select('*');

      if (userEmail) {
        query = query.eq('email', userEmail);
      } else if (userPhone) {
        // Auth phone may lack '+'; profile stores E.164 with '+'
        const e164 = normalizePhoneE164(userPhone);
        query = e164
          ? query.or(`phone.eq.${e164},phone_normalized.eq.${e164},phone.eq.${userPhone}`)
          : query.eq('phone', userPhone);
      }

      const { data: profileByContact, error: contactError } = await query;

      if (contactError) {
        return { data: null, error: new Error(contactError.message) };
      }

      if (profileByContact && profileByContact.length > 0) {
        const existingProfile = profileByContact[0];

        // Link profile to current auth user_id
        const { data: updated, error: updateError } = await supabase
          .from('user_profile')
          .update({ user_id: userId })
          .eq('user_id', existingProfile.user_id)
          .select()
          .single();

        if (updateError) {
          // Return existing profile even if update failed
          return { data: existingProfile, error: null };
        }

        return { data: updated, error: null };
      }
    }

    // No profile found at all
    return { data: null, error: new Error('Profile not found') };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: ProfileUpdateData
): Promise<{ data: UserProfile | null; error: Error | null }> {
  try {
    const normalizedUpdates: ProfileUpdateData = {
      ...updates,
      ...(updates.phone !== undefined
        ? { phone: normalizePhoneE164(updates.phone) || updates.phone || undefined }
        : {}),
    };

    const { data, error } = await supabase
      .from('user_profile')
      .update(normalizedUpdates)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Upload profile image to Supabase Storage via Edge Function
 * The Edge Function handles user identification and bypasses RLS
 */
export async function uploadProfileImage(
  _userId: string, // Kept for backward compatibility, Edge Function uses session
  file: File
): Promise<{ url: string | null; error: Error | null }> {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { url: null, error: new Error('Invalid file type. Only JPG, PNG, and WebP are allowed.') };
    }

    // Validate file size (max 5MB, bucket limit is 10MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { url: null, error: new Error('File size too large. Maximum size is 5MB.') };
    }

    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { url: null, error: new Error('Not authenticated') };
    }

    // Use Edge Function to upload (bypasses storage RLS issues)
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-profile-image`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return { url: null, error: new Error(result.error || 'Upload failed') };
    }

    return { url: result.url, error: null };
  } catch (error: any) {
    return { url: null, error };
  }
}

/**
 * Delete profile image from Supabase Storage
 */
export async function deleteProfileImage(userId: string): Promise<{ error: Error | null }> {
  try {
    // List all files in user's folder
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list(userId);

    if (listError) {
      return { error: new Error(listError.message) };
    }

    if (!files || files.length === 0) {
      return { error: null }; // No files to delete
    }

    // Delete avatar files only
    const avatarFiles = files.filter(file => file.name.startsWith('avatar_'));
    if (avatarFiles.length === 0) {
      return { error: null };
    }

    const filePaths = avatarFiles.map((file) => `${userId}/${file.name}`);
    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(filePaths);

    if (deleteError) {
      return { error: new Error(deleteError.message) };
    }

    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Create initial user profile (used during registration)
 */
export async function createUserProfile(
  userId: string,
  data: {
    email: string;
    fullname: string;
    phone?: string;
    country_code?: string;
  }
): Promise<{ data: UserProfile | null; error: Error | null }> {
  try {
    const username = generateUsername(data.fullname);

    // Use upsert to handle conflicts gracefully
    const { data: profile, error } = await supabase
      .from('user_profile')
      .upsert({
        user_id: userId,
        email: data.email,
        fullname: data.fullname,
        user_name: username,
        phone: normalizePhoneE164(data.phone) || null,
        country_code: data.country_code || null,
        slug: 'placeholder', // DB trigger auto-generates actual slug
      }, {
        onConflict: 'user_id',
        ignoreDuplicates: false, // Update if exists
      })
      .select()
      .single();

    if (error) {
      // If it's a conflict error, try to fetch the existing profile
      if (error.code === '23505' || error.message.includes('duplicate')) {
        const { data: existing, error: fetchError } = await supabase
          .from('user_profile')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (existing) {
          return { data: existing, error: null };
        }

        return { data: null, error: new Error(fetchError?.message || 'Profile conflict') };
      }

      return { data: null, error: new Error(error.message) };
    }

    return { data: profile, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Check if profile is complete (relaxed)
 * Only fullname is required — other fields are collected progressively
 * via contextual nudges and enforcement gates per the UX strategy.
 */
export function isProfileComplete(profile: UserProfile | null): boolean {
  if (!profile) return false;

  return !!profile.fullname && profile.fullname.trim() !== '';
}

/**
 * Get profile completion percentage
 */
export function getProfileCompletionPercentage(profile: UserProfile | null): number {
  if (!profile) return 0;

  const fields = [
    profile.fullname,
    profile.email,
    profile.phone,
    profile.dob,
    profile.country_id,
    profile.state,
    profile.city,
    profile.image_url,
  ];

  const completedFields = fields.filter((field) => field !== null && field !== '').length;
  return Math.round((completedFields / fields.length) * 100);
}
